import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useAuth } from '../auth.js';
import { getLocale, setLocale, t, type Locale } from '../i18n.js';

type View = 'menu' | 'audit';

export function SettingsDrawer({ onClose, quotaLeft }: { onClose: () => void; quotaLeft: number | null }) {
  const [view, setView] = useState<View>('menu');
  const [writes, setWrites] = useState<{ ts: number; action: string; result: string }[]>([]);
  const { refresh } = useAuth();
  const locale = getLocale();

  useEffect(() => {
    if (view === 'audit') {
      void api.getWrites(20).then((r) => setWrites(r.entries));
    }
  }, [view]);

  const [docUrls, setDocUrls] = useState<{ rules: string; telescope: string }>({ rules: '', telescope: '' });

  useEffect(() => {
    void api.getHouseConfig()
      .then((c) => setDocUrls({ rules: c.rules_url ?? '', telescope: c.telescope_url ?? '' }))
      .catch(() => {});
  }, []);

  const [energyPower, setEnergyPower] = useState('');
  const [energyTariff, setEnergyTariff] = useState('');
  const [energySaved, setEnergySaved] = useState(false);
  const [energyError, setEnergyError] = useState(false);

  useEffect(() => {
    void api.getEnergyConfig().then((c) => {
      setEnergyPower(c.power_kw === null ? '' : String(c.power_kw));
      setEnergyTariff(c.tariff_eur_per_kwh === null ? '' : String(c.tariff_eur_per_kwh));
    }).catch(() => {});
  }, []);

  const saveEnergy = async () => {
    setEnergyError(false);
    setEnergySaved(false);
    const power = energyPower.trim() === '' ? null : Number(energyPower);
    const tariff = energyTariff.trim() === '' ? null : Number(energyTariff);
    if ((power !== null && (!Number.isFinite(power) || power <= 0)) ||
        (tariff !== null && (!Number.isFinite(tariff) || tariff <= 0))) {
      setEnergyError(true);
      return;
    }
    try {
      await api.setEnergyConfig({ power_kw: power, tariff_eur_per_kwh: tariff });
      setEnergySaved(true);
    } catch {
      setEnergyError(true);
    }
  };

  const [netatmo, setNetatmo] = useState<{ configured: boolean; connected: boolean } | null>(null);
  const [netatmoError, setNetatmoError] = useState(false);

  useEffect(() => {
    void api.netatmoStatus().then(setNetatmo).catch(() => {});
  }, []);

  const connectNetatmo = async () => {
    setNetatmoError(false);
    try {
      const { url } = await api.netatmoAuthorizeUrl();
      window.location.href = url;
    } catch {
      setNetatmoError(true);
    }
  };

  const disconnectNetatmo = async () => {
    setNetatmoError(false);
    try {
      await api.netatmoDisconnect();
      setNetatmo({ configured: true, connected: false });
    } catch {
      setNetatmoError(true);
    }
  };

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={t('settings.title')}>
        <div className="sheet-grab" />
        <div className="sheet-title">{t('settings.title')}</div>
        {view === 'menu' && (
          <div className="drawer-list">
            <button
              className="drawer-row"
              onClick={() => setView('audit')}
            >
              <span>{t('settings.recent_actions')}</span><span className="arrow">›</span>
            </button>
            <div className="drawer-row" style={{ cursor: 'default' }}>
              <span>{t('settings.api_quota')}</span>
              <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                {quotaLeft == null ? t('settings.calls_left_unknown') : t('settings.calls_left', { n: quotaLeft.toLocaleString() })}
              </span>
            </div>
            <div className="drawer-row" style={{ cursor: 'default' }}>
              <span>{t('settings.language')}</span>
              <span style={{ display: 'flex', gap: 6 }}>
                <LangChip current={locale} value="en" label={t('settings.lang_english')} />
                <LangChip current={locale} value="fr" label={t('settings.lang_french')} />
              </span>
            </div>
            <DocUrlRow
              label={t('settings.house_rules')}
              initial={docUrls.rules}
              save={(url) => api.setHouseConfig({ rules_url: url })}
            />
            <DocUrlRow
              label={t('settings.telescope')}
              initial={docUrls.telescope}
              save={(url) => api.setHouseConfig({ telescope_url: url })}
            />
            <div className="drawer-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6, cursor: 'default' }}>
              <span>{t('settings.energy')}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={energyPower}
                  onChange={(e) => { setEnergyPower(e.target.value); setEnergySaved(false); }}
                  placeholder={t('settings.energy_power')}
                  aria-label={t('settings.energy_power')}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={energyTariff}
                  onChange={(e) => { setEnergyTariff(e.target.value); setEnergySaved(false); }}
                  placeholder={t('settings.energy_tariff')}
                  aria-label={t('settings.energy_tariff')}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <button className="chip" onClick={() => void saveEnergy()}>
                  {t('settings.energy_save')}
                </button>
              </div>
              {energySaved && <span style={{ color: 'var(--muted)', fontSize: 11 }}>{t('settings.energy_saved')}</span>}
              {energyError && <span style={{ color: 'var(--danger)', fontSize: 11 }}>{t('settings.energy_invalid')}</span>}
            </div>
            <div className="drawer-row" style={{ cursor: 'default' }}>
              <span>{t('settings.camera')}</span>
              <span>
                {netatmo == null ? null : !netatmo.configured ? (
                  <span style={{ color: 'var(--muted)', fontSize: 12 }}>{t('settings.camera_not_configured')}</span>
                ) : netatmo.connected ? (
                  <button className="chip" onClick={() => void disconnectNetatmo()}>
                    {t('settings.camera_disconnect')}
                  </button>
                ) : (
                  <button className="chip" onClick={() => void connectNetatmo()}>
                    {t('settings.camera_connect')}
                  </button>
                )}
              </span>
              {netatmoError && (
                <span style={{ color: 'var(--danger)', fontSize: 11 }}>{t('settings.camera_error')}</span>
              )}
            </div>
            <button
              className="drawer-row is-danger"
              onClick={async () => { await api.logout(); await refresh(); onClose(); }}
            >
              <span>{t('settings.sign_out')}</span><span className="arrow">›</span>
            </button>
          </div>
        )}
        {view === 'audit' && (
          <>
            <button
              className="drawer-row"
              onClick={() => setView('menu')}
              style={{ paddingLeft: 0 }}
            >{t('settings.back')}</button>
            <table style={{ width: '100%', fontSize: 12, marginTop: 8 }}>
              <thead><tr>
                <th align="left">{t('settings.col_when')}</th>
                <th align="left">{t('settings.col_action')}</th>
                <th align="left">{t('settings.col_result')}</th>
              </tr></thead>
              <tbody>
                {writes.map((w, i) => (
                  <tr key={i}>
                    <td>{new Date(w.ts * 1000).toLocaleString()}</td>
                    <td>{w.action}</td>
                    <td>{w.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

/** One editable document URL (house document, telescope…) with its own save feedback. */
function DocUrlRow(
  { label, initial, save }: { label: string; initial: string; save: (url: string) => Promise<unknown> },
) {
  const [url, setUrl] = useState(initial);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);

  // The drawer loads both URLs asynchronously; adopt the value once it arrives.
  useEffect(() => { setUrl(initial); }, [initial]);

  const onSave = async () => {
    setError(false);
    setSaved(false);
    const trimmed = url.trim();
    if (trimmed !== '' && !/^https?:\/\/.+/.test(trimmed)) {
      setError(true);
      return;
    }
    try {
      await save(trimmed);
      setSaved(true);
    } catch {
      setError(true);
    }
  };

  return (
    <div className="drawer-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6, cursor: 'default' }}>
      <span>{label}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="url"
          value={url}
          onChange={(e) => { setUrl(e.target.value); setSaved(false); }}
          placeholder={t('settings.doc_placeholder')}
          aria-label={label}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button className="chip" onClick={() => void onSave()}>
          {t('settings.doc_save')}
        </button>
      </div>
      {saved && <span style={{ color: 'var(--muted)', fontSize: 11 }}>{t('settings.doc_saved')}</span>}
      {error && <span style={{ color: 'var(--danger)', fontSize: 11 }}>{t('settings.doc_invalid')}</span>}
    </div>
  );
}

function LangChip({ current, value, label }: { current: Locale; value: Locale; label: string }) {
  const active = current === value;
  return (
    <button
      className={`chip${active ? ' is-active' : ''}`}
      onClick={() => setLocale(value)}
      style={{ padding: '4px 10px', fontSize: 11 }}
    >{label}</button>
  );
}
